import { WaveformService } from "libs/xplat/core/src/lib/services/waveform.service";
import { AfterViewInit, Component, ElementRef, Input, ViewChild, inject, EventEmitter, Output, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, UserService } from "@mypxplat/xplat/core";
import type WaveSurfer from "wavesurfer.js";
import { AppService, WaveformPeaksService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { Subscription } from "rxjs";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-waveform-container",
  templateUrl: "waveform-container.component.html",
  host: {
    "(document:keyup)": "keyupEvent($event)",
  },
  standalone: false,
})
export class WaveformContainerComponent extends BaseComponent implements AfterViewInit {
  constructor(
    private waveformService: WaveformService,
    public userService: UserService,
    public communityService: WebCommunityService,
    public appService: AppService,
    private peaksService: WaveformPeaksService
  ) {
    super(userService);
  }

  @Input() selectedWaveformFile: any;
  @Input() onTrackRendered: Function;
  @Input() waveformClicked: Function;
  @Input() onTimeUpdate: Function;
  @Input() playAtTime: Function;
  @Input() setIsPlaying: Function;
  @Input() trackRendered: boolean;
  @Input() keyupEvent: Function;
  @Input() isPlaying: boolean;
  @Input() audioPaused: EventEmitter<any> = new EventEmitter();
  @Output() audioPlayed: EventEmitter<any> = new EventEmitter();
  @ViewChild("waveformContainer", { static: false }) waveformEl: ElementRef;
  private waveform: WaveSurfer;
  private toggleAudioSubscription: Subscription;
  private playAtTimeSubscription: Subscription;
  private pauseSubscription: Subscription;
  private destroyed = false;

  ngAfterViewInit(): void {
    this.createWaveformContainer();
    this.toggleAudioSubscription = this.waveformService.toggleAudio$.subscribe((filename) => {
      if (filename == this.selectedWaveformFile.filename) this.toggleAudio();
    });

    // The player is created once the peaks are in, so these can fire before it exists.
    this.playAtTimeSubscription = this.waveformService.playAtTime$.subscribe((time: number) => {
      if (!this.waveform) return;
      this.waveform.setTime(time);
      this.waveform.play();
    });

    this.pauseSubscription = this.waveformService.pause$.subscribe(() => {
      this.waveform?.pause();
    });
  }

  /**
   * Draws from the server's precomputed peaks (DENG-905) when it has them, so WaveSurfer streams
   * the audio through a media element instead of downloading and decoding the whole file first.
   * The first view of an older file waits while the server generates them (usually 5-15s, behind
   * the existing "rendering audio" spinner); if they never come, WaveSurfer decodes the audio
   * itself as it always has.
   */
  async createWaveformContainer() {
    const file = this.selectedWaveformFile;
    // Nothing in storage to draw or play; waveform-comments says so in place of the waveform.
    if (file?.storage_missing) return;
    const peaksLoad = this.peaksService.load({ id: file.id, url: file.url }, () => this.destroyed || this.selectedWaveformFile !== file).catch(() => null);

    try {
      // wavesurfer is loaded on demand so it stays out of the main bundle; it downloads alongside the peaks.
      const [peaks, { default: WaveSurfer }, { default: RegionsPlugin }, { default: TimelinePlugin }] = await Promise.all([
        peaksLoad,
        import("wavesurfer.js"),
        import("wavesurfer.js/dist/plugins/regions.esm.js"),
        import("wavesurfer.js/dist/plugins/timeline.esm.js"),
      ]);
      if (this.destroyed || this.selectedWaveformFile !== file) return;

      const topTimeline = TimelinePlugin.create({
        insertPosition: "beforebegin",
        style: {
          color: "#2D5B88",
        },
      });
      this.waveform = WaveSurfer.create({
        container: this.waveformEl.nativeElement,
        height: 150,
        waveColor: "#E51A89",
        url: file.url,
        // Troughs below the line, crests above it: WaveSurfer draws channel 0 up and channel 1 down.
        ...(peaks ? { peaks: [peaks.max, peaks.min], duration: peaks.duration } : {}),
        plugins: [topTimeline],
      });
      this.waveform.on("click", () => {
        this.waveformClicked();
      });
      const wsRegions = this.waveform.registerPlugin(RegionsPlugin.create());

      this.waveform.on("decode", () => {
        wsRegions.addRegion({
          start: 0,
          color: "rgba(35, 35, 35, 0.5)",
          drag: false,
          resize: false,
        });
      });

      this.waveform.on("timeupdate", (currentTime) => {
        this.onTimeUpdate(currentTime, this.waveform);
      });

      this.waveform.on("ready", () => {
        this.onTrackRendered();
      });
    } catch (error) {
      console.error(error);
    }
  }

  onPlayButtonClick = (start?: number) => {
    this.setIsPlaying(true);
    if (start) {
      this.waveform.setTime(start);
    }
    this.waveform.play();
  };

  onPauseButtonClick() {
    this.setIsPlaying(false);
    this.waveform.pause();
  }

  toggleAudio = () => {
    if (this.waveform) {
      if (this.isPlaying) {
        this.onPauseButtonClick();
        this.audioPaused.next({
          file: this.selectedWaveformFile.url,
          audio: this.waveform,
        });
        this.communityService.currentlyPlayingWaveformComponent = undefined;
      } else {
        this.waveformService.calculateProgress(this.waveform.getCurrentTime(), this.waveform.getDuration()) >= 100 ? this.playAtTime(0) : this.onPlayButtonClick();
        this.audioPlayed.next({
          file: this.selectedWaveformFile.url,
          audio: this.waveform,
        });
        this.communityService.currentlyPlayingWaveformComponent = this;
      }
    }
  };

  ngOnDestroy() {
    this.destroyed = true;
    this.toggleAudioSubscription.unsubscribe();
    this.playAtTimeSubscription.unsubscribe();
    this.pauseSubscription.unsubscribe();
  }
}
